import { AvatarGroupV2, AvatarV2, Progress, TooltipV2 } from '@cimplico/components';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useIsFirmRole } from '../../../hooks/use-is-firm-role';
import { useUserContext } from '../../../providers/UserProvider';
import { entityHexColourCache } from '../../../queries/cache';
import { Workpaper, WorkpaperRoleEnum } from '../../../types/workpaper';
import { formatMixedCase, parseTitle } from '../../../utilities/formatting';
import plural from '../../../utilities/plural';
import { useFirmContext } from '../../../providers/FirmProvider';

type OverdueWorkpaperRowProps = {
    workpaper: Workpaper;
};

function OverdueWorkpaperRow({ workpaper }: OverdueWorkpaperRowProps): JSX.Element {
    const { isAdmin } = useIsFirmRole();
    const { shortId } = useFirmContext();
    const { id } = useUserContext();

    const canAccess = isAdmin ? true : workpaper.private ? workpaper.assigned.find((a) => a.id === id) : true;
    const roleOrder = [WorkpaperRoleEnum.APPROVER, WorkpaperRoleEnum.REVIEWER, WorkpaperRoleEnum.PREPARER];
    const badge = (
        <div className="bg-green-600 rounded-full p-0.5">
            <CheckIcon className="w-[5px] h-[5px] text-white" />
        </div>
    );

    return (
        <div className="grid grid-cols-[1fr_0.25fr_0.25fr_0.25fr] gap-2 items-center">
            <div className="flex gap-x-2">
                <AvatarV2
                    alt={`${parseTitle(workpaper.entity.name)}`}
                    size="sm"
                    color={entityHexColourCache[workpaper.entity.id]}
                    rounded={false}
                />
                <div className="flex flex-col text-xs">
                    <div className="flex gap-x-1">
                        {canAccess ? (
                            <Link to={`/${shortId}/workpapers/${workpaper.id}/dashboard`}>
                                <p className="text-indigo-600 dark:text-indigo-400 font-medium cursor-pointer">{workpaper.name}</p>
                            </Link>
                        ) : (
                            <p className="text-gray-600 dark:text-gray-400 font-medium">{workpaper.name}</p>
                        )}
                        {workpaper.private && (
                            <div>
                                <TooltipV2 message="This workpaper is private.">
                                    <LockClosedIcon className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                                </TooltipV2>
                            </div>
                        )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">{workpaper.entity.name}</p>
                </div>
            </div>
            <div className="flex justify-center">
                <AvatarGroupV2 size="xs">
                    {workpaper.assigned
                        .filter((a) => a && a.firstName && a.lastName)
                        .sort((a, b) => roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role))
                        .sort((a) => (a.certified ? -1 : 1))
                        .map((assignee) => {
                            return (
                                <AvatarV2
                                    size="mini"
                                    key={assignee.id}
                                    src={assignee.avatarUrl}
                                    alt={assignee.firstName[0] + assignee.lastName[0]}
                                    color={assignee.profileHexColour}
                                    badge={assignee.certified && badge}
                                    tooltip={`${assignee.firstName} ${assignee.lastName} (${formatMixedCase(assignee.role)})`}
                                />
                            );
                        })}
                </AvatarGroupV2>
            </div>
            <div className="flex justify-end">
                <Progress type="circle" showProgressLabel percent={workpaper.progressPercent} />
            </div>
            <div className="flex justify-end text-sm text-red-600 dark:text-red-400">
                {workpaper.overdueDays} {plural(workpaper.overdueDays as number, 'day', 'days')}
            </div>
        </div>
    );
}

export default OverdueWorkpaperRow;
